import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';
import { APIError } from '~/src/utils/error';
import { useCurrentOrgFprint } from '../../user';
import { fieldMappingService } from '../services';
import type { FieldMappingResponse, FieldMappingData } from '../types';
import { keys as projectKeys } from '../../project/keys';
import { Project, sanitizeFieldName } from '../../project';
import { MappingStatus } from '../types';
import {
  createOptimisticFieldMappingUpdate,
  rollbackOptimisticUpdate,
} from './useOptimisticFieldMappingUpdate';

type CreateFieldMappingVariables = {
  projectId: number;
  data: FieldMappingData;
};

type CreateFieldMappingContext = {
  previousProject?: Project;
};

export const useCreateFieldMapping = (
  options: UseMutationOptions<
    FieldMappingResponse,
    APIError,
    CreateFieldMappingVariables,
    CreateFieldMappingContext
  > = {},
) => {
  const orgFprint = useCurrentOrgFprint();
  const queryClient = useQueryClient();

  return useMutation<
    FieldMappingResponse,
    APIError,
    CreateFieldMappingVariables,
    CreateFieldMappingContext
  >({
    mutationFn: ({ projectId, data }) =>
      fieldMappingService.createFieldMapping(orgFprint, projectId, data),
    onMutate: async ({ projectId, data }) => {
      const sanitizedTargetName = sanitizeFieldName(data.target_name);

      return createOptimisticFieldMappingUpdate(
        queryClient,
        projectId,
        (field) => {
          if (field.fid === sanitizedTargetName) {
            return {
              ...field,
              field_mapping_metadata: {
                source_field: data.source_name,
                status: data.status ?? MappingStatus.USER_CREATED,
                // Placeholder ID until server responds
                field_mapping_id: -1,
              },
            };
          }
          return field;
        },
      );
    },
    onError: (err, variables, context) => {
      rollbackOptimisticUpdate(
        queryClient,
        variables.projectId,
        context?.previousProject,
      );
      options.onError?.(err, variables, context);
    },
    onSuccess: (data, variables, context) => {
      // Update the placeholder ID with the real server ID
      const project = queryClient.getQueryData<Project>(
        projectKeys.detail(variables.projectId),
      );
      if (project) {
        const sanitizedTargetName = sanitizeFieldName(data.target_name);
        queryClient.setQueryData<Project>(
          projectKeys.detail(variables.projectId),
          {
            ...project,
            stack_data: {
              ...project.stack_data,
              fields: project.stack_data.fields.map((field) => {
                if (
                  field.fid === sanitizedTargetName &&
                  field.field_mapping_metadata?.field_mapping_id === -1
                ) {
                  return {
                    ...field,
                    field_mapping_metadata: {
                      ...field.field_mapping_metadata,
                      field_mapping_id: data.id,
                    },
                  };
                }
                return field;
              }),
            },
          },
        );
      }

      options.onSuccess?.(data, variables, context);
    },
  });
};
